<template>
  <div v-if="store.loaded" class="Cart" :class="{ 'Cart--Empty': isEmpty }">
    <CartEmpty v-if="isEmpty" />
    <CartSummary v-show="!isEmpty" :items="items" />
    <CartReceipt v-show="!isEmpty" :items="items" />
    <Upsell v-if="upsellIsReady" v-show="!isEmpty" :selection-listener="false" :items="store.upsell.items" :title="store.terms.upsell.title" location="upsell" />
    <CartNudge v-show="!isEmpty" />
    <CartCheckout v-show="!isEmpty" />
    <CartBrokenPopup v-show="showBrokenCartPopup && !isEmpty" />
  </div>
</template>

<script setup>
import { onMounted, computed, watch } from 'vue';
import { useCartStore } from './Composables/Store';
import { pubSub } from '@utils/PubSub';
import { emailDlPush } from '@utils/global-functions';
import CartEmpty from './CartEmpty.vue';
import CartNudge from './CartNudge.vue';
import CartCheckout from './CartCheckout.vue';
import CartSummary from './CartSummary.vue';
import CartReceipt from './CartReceipt.vue';
import CartBrokenPopup from './CartBrokenPopup.vue';
import Upsell from '../Global/Upsell.vue';

const props = defineProps({
  terms: Object,
  freeShipThresh: Number,
  quantityPolicy: Number,
  upsellItems: String,
  receiveRecommendations: {
    type: [String, Boolean],
    default: false,
  },
});

const store = useCartStore();

store.fetchWindow();
store.cartListeners();

const items = computed(() => store.getLineItems);
const upsellIsReady = computed(() => store.upsellIsReady);
const isEmpty = computed(() => {
  return store.loaded && store.getLineItems.length <= 0;
});
const receiveRecommendations = JSON.parse(props?.receiveRecommendations) ?? false;

const handleRecs = () => {
  pubSub.unsubscribe('dy:cart:upsell:init', handleRecs, handleFallbackRecs);
  const global = window?.Brooklinen ?? {};
  const acceptRecs = receiveRecommendations;
  const recommendations = global?.cartUpsell ?? [];
  const ignoreRecs = recommendations.length <= 0 || !acceptRecs;
  if (ignoreRecs) return;

  handleUpsell(recommendations, true);
};

const handleFallbackRecs = () => {
  pubSub.unsubscribe('dy:cart:upsell:init', handleRecs, handleFallbackRecs);
  const items = props?.upsellItems.split(',') ?? [];
  if (items.length <= 0) return;

  handleUpsell();
};

const handleUpsell = async (items = false, overwritten = false) => {
  store.$state.upsell.loaded = false;
  store.$state.upsell.items = await store.fetchUpsellItems(items);
  store.$state.upsell.loaded = true;
  store.$state.upsell.overwritten = overwritten;
};

watch(() => items, store.onChange, { deep: true });
pubSub.subscribe('dy:cart:upsell:init', handleRecs, handleFallbackRecs);

const showBrokenCartPopup = computed(() => {
  return store.$state.show_broken_cart_popup;
});
onMounted(async () => {
  await store.checkToteStatus();
  const data = await store.fetchEndpoint();
  store.$state = { ...store.$state, ...data };
  store.$state.quantity_policy = props.quantityPolicy;
  store.$state.terms = props.terms;
  store.$state.free_ship_thresh = props.freeShipThresh;
  store.$state.loaded = true;
  store.$state.upsell.allow_overwrite = receiveRecommendations;
  store.$state.upsell.items = props?.upsellItems.split(',') ?? [];
  await store.setAttributes();
  if (!receiveRecommendations) handleUpsell();
  emailDlPush();
});
</script>
